import { useCallback, useMemo, useState } from "react";

import { ChatActions } from "src/base-components/Chat/ChatActions";
import { ChatMessage } from "src/base-components/Chat/ChatMessage";
import { ChatTextContent as CopilotTextContent } from "src/base-components/Chat/ChatTextContent";
import { normalizeAnimationSpeed } from "src/base-components/Chat/utils";
import {
  ConversationMessage,
  ConversationMessageRoleEnum,
  FeatureQueryConversationContextContextTypeEnum,
  ToolUse,
} from "src/clients/copilot";
import { BeMappedNode } from "src/constants/NodeDataTypes";
import { EntityMention } from "src/copilot/EntityMention";
import { EventMention } from "src/copilot/EventMention";
import { NodeMention } from "src/copilot/NodeMention";
import { NodeSuggestion, NodeSuggestionType } from "src/copilot/NodeSuggestion";
import {
  QuerySuggestion,
  QuerySuggestionType,
} from "src/copilot/QuerySuggestion";
import { ToolApprovalCard } from "src/copilot/ToolApprovalCard";
import {
  FeatureQueryResource,
  getEntity,
  getEvent,
  NodeResource,
  ResourceType,
  replaceMentions,
  replaceSuggestions,
} from "src/copilot/utils";
import {
  tracker,
  trackingEvents,
} from "src/instrumentation/customTrackingEvents";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { logger } from "src/utils/logger";
import { errorMessage } from "src/utils/stringUtils";

type ReactionStatus = "liked" | "disliked";

export type RenderMentionFnArgs = {
  mention: string;
  key: number;
  delay: number;
};

type Props = {
  message: ConversationMessage;
  resourceData: NodeResource | FeatureQueryResource;
  resourceType: ResourceType;
  conversationId: string;
  completionId: string | null;
  onApply: (suggestion: NodeSuggestionType | QuerySuggestionType) => void;
  onRegenerate: VoidFunction;
  animate: boolean;
  isLastMessage?: boolean;
  onToolApproval?: (toolUse: ToolUse, approved: boolean) => void;
  isLoading?: boolean;
  canEditFlowVersion: boolean;
};

export type RenderTextContentFnArgs = {
  animate: boolean;
  content: string;
  delay: number;
  key?: number;
  inline?: boolean;
};

export type RenderSuggestionFnArgs = {
  suggestion: string;
  resourceData: NodeResource | FeatureQueryResource;
  resourceType: ResourceType;
  key: number;
  onApply: (suggestion: NodeSuggestionType | QuerySuggestionType) => void;
  delay: number;
  canEditFlowVersion: boolean;
};

export type RenderToolApprovalFnArgs = {
  toolUse: ToolUse;
  onApprove: () => void;
  onDeny: () => void;
  disabled?: boolean;
  delay: number;
  key: number | string;
};

export const renderTextContent = ({
  key,
  inline,
  ...args
}: RenderTextContentFnArgs) => (
  <CopilotTextContent {...args} key={key} inline={inline} />
);

export const renderToolApproval = ({
  toolUse,
  onApprove,
  onDeny,
  disabled,
  delay,
  key,
}: RenderToolApprovalFnArgs) => (
  <ToolApprovalCard
    key={key}
    delay={delay}
    disabled={disabled}
    toolUse={toolUse}
    onApprove={onApprove}
    onDeny={onDeny}
  />
);

export const renderSuggestion = ({
  suggestion,
  resourceData,
  resourceType,
  key,
  onApply,
  delay,
  canEditFlowVersion,
}: RenderSuggestionFnArgs) => {
  try {
    const suggestionObj = JSON.parse(suggestion);
    if (
      "query" in suggestionObj &&
      resourceType ===
        FeatureQueryConversationContextContextTypeEnum.FEATURE_QUERY
    ) {
      const querySuggestion: QuerySuggestionType = {
        query: suggestionObj.query,
      };
      return (
        <QuerySuggestion
          key={key}
          delay={delay}
          suggestion={querySuggestion}
          onApply={onApply}
        />
      );
    }
    if (
      "meta" in suggestionObj &&
      "name" in suggestionObj &&
      "type" in suggestionObj &&
      resourceType === "node" &&
      "node" in resourceData
    ) {
      const nodeSuggestion: NodeSuggestionType = {
        nodeId: resourceData.node.id,
        meta: suggestionObj.meta,
        name: suggestionObj.name,
        type: suggestionObj.type,
      };
      return (
        <NodeSuggestion
          key={key}
          canEditFlowVersion={canEditFlowVersion}
          delay={delay}
          nodeSuggestion={nodeSuggestion}
          originalNode={resourceData.node}
          onApply={onApply}
        />
      );
    } else {
      throw new Error(suggestionObj);
    }
  } catch (e) {
    logger.error("Invalid node suggestion format", e);
    return (
      <CopilotTextContent
        key={key}
        content={`Unable to load node suggestion: ${errorMessage(e)}`}
        delay={delay}
      />
    );
  }
};

const getPendingToolUse = (
  message: ConversationMessage,
  isLastMessage: boolean,
): ToolUse | null => {
  if (!isLastMessage) return null;
  if (message.role !== ConversationMessageRoleEnum.COPILOT) return null;
  if (!message.tool_use) return null;
  if (message.tool_use.type !== "request") return null;

  return message.tool_use;
};

export const ConversationMessageItem: React.FC<Props> = ({
  message,
  resourceData,
  resourceType,
  conversationId,
  completionId,
  onApply,
  onRegenerate,
  animate,
  isLastMessage = false,
  onToolApproval,
  isLoading,
  canEditFlowVersion,
}) => {
  const { orgId } = useWorkspaceContext();
  const [reactionStatus, setReaction] = useState<ReactionStatus | null>(null);
  const isCopilotMessage = message.role === ConversationMessageRoleEnum.COPILOT;

  const getFeatureQueryMentionedTag = useCallback(
    (mention: string, key: number, delay: number) => {
      const featureQueryResource = resourceData as FeatureQueryResource;

      const entity = getEntity(featureQueryResource, mention);
      if (entity) {
        return [<EntityMention key={key} delay={delay} entity={entity} />];
      }

      const event = getEvent(featureQueryResource, mention);

      return [<EventMention key={key} delay={delay} event={event} />];
    },
    [resourceData],
  );

  const renderMention = useCallback(
    ({ mention, key, delay }: RenderMentionFnArgs) => {
      if (
        resourceType ===
        FeatureQueryConversationContextContextTypeEnum.FEATURE_QUERY
      ) {
        return getFeatureQueryMentionedTag(mention, key, delay);
      }
      const node =
        (resourceData as NodeResource).nodesArray.find(
          (n: BeMappedNode) =>
            n.data.label.toLowerCase() === mention.toLowerCase(),
        ) ?? null;
      return [<NodeMention key={key} delay={delay} node={node} />];
    },
    [getFeatureQueryMentionedTag, resourceData, resourceType],
  );

  const pendingToolUse = getPendingToolUse(message, isLastMessage);

  const renderedContent = useMemo(() => {
    const { parts: responseBlocks, nextKey } = replaceMentions({
      renderMention,
      content: message.content,
      resourceType,
    });
    let baseAnimationDelay = 0.1;
    let baseKey = nextKey;
    const content = responseBlocks.map((block) => {
      if (typeof block === "string") {
        const parsed = replaceSuggestions({
          baseKey,
          content: block,
          resourceData,
          resourceType,
          onApply,
          animate,
          baseAnimationDelay,
          canEditFlowVersion,
        });
        baseKey += parsed.length;
        baseAnimationDelay += normalizeAnimationSpeed(block.length);
        return parsed;
      }
      return block;
    });

    if (pendingToolUse && onToolApproval) {
      content.push(
        renderToolApproval({
          key: "tool-approval",
          toolUse: pendingToolUse,
          onApprove: () => onToolApproval(pendingToolUse, true),
          onDeny: () => onToolApproval(pendingToolUse, false),
          disabled: isLoading,
          delay: animate ? baseAnimationDelay : 0,
        }),
      );
    }

    return content;
  }, [
    renderMention,
    message.content,
    pendingToolUse,
    onToolApproval,
    resourceData,
    resourceType,
    onApply,
    animate,
    canEditFlowVersion,
    isLoading,
  ]);

  const handleReaction = (status: ReactionStatus) => {
    const { node } = resourceData as NodeResource;
    if (!completionId) return;

    let recordedReaction;
    if (status === reactionStatus) {
      setReaction(null);
      recordedReaction = "unset" as const;
    } else {
      setReaction(status);
      recordedReaction = status;
    }

    tracker.emit(
      trackingEvents.reactCopilotResponse({
        organization_id: orgId,
        node_id: "id" in node ? node?.id : "",
        conversation_id: conversationId,
        completion_id: completionId,
        reaction: recordedReaction,
      }),
    );
  };

  return (
    <ChatMessage
      isLegacy
      renderActions={
        isCopilotMessage && completionId
          ? () => (
              <ChatActions
                reactionStatus={reactionStatus}
                onDislike={() => handleReaction("disliked")}
                onLike={() => handleReaction("liked")}
                onRegenerate={onRegenerate}
              />
            )
          : undefined
      }
      role={isCopilotMessage ? "assistant" : "user"}
    >
      {renderedContent}
    </ChatMessage>
  );
};
